"use client";

import { useParams, usePathname, useRouter } from "next/navigation";
import { channelSlugToFlagIso, iso2ToFlagEmoji } from "@/lib/flag-emoji";
import { setGeoPrefCookie } from "@/lib/geo/geo-pref-client";
import { cn } from "@/lib/utils";

export const ChannelSelect = ({
	channels,
	className,
	id,
	showOptionEmoji = true,
	regionLabels,
	"aria-label": ariaLabel,
	"aria-labelledby": ariaLabelledBy,
}: {
	channels: { id: string; name: string; slug: string; currencyCode: string }[];
	className?: string;
	id?: string;
	showOptionEmoji?: boolean;
	/** When set, overrides display text for `eu` / `us` options (e.g. nav.regionEu / regionUs). */
	regionLabels?: { eu: string; us: string };
	"aria-label"?: string;
	"aria-labelledby"?: string;
}) => {
	const router = useRouter();
	const pathname = usePathname();
	const params = useParams<{ locale?: string; channel?: string }>();
	const currentChannel = params.channel ?? "eu";

	const fallbackChannels = [
		{ id: "eu", name: "EU", slug: "eu", currencyCode: "EUR" },
		{ id: "us", name: "US", slug: "us", currencyCode: "USD" },
	];
	const hasProvidedChannels = channels.length > 0;
	const effectiveChannels = hasProvidedChannels
		? channels
		: fallbackChannels.some((channel) => channel.slug === currentChannel)
			? fallbackChannels
			: [
					...fallbackChannels,
					{
						id: currentChannel,
						name: currentChannel.toUpperCase(),
						slug: currentChannel,
						currencyCode: currentChannel.toUpperCase(),
					},
				];

	const getChannelLabel = (channel: { name: string; slug: string; currencyCode: string }) => {
		const normalizedSlug = channel.slug.toLowerCase();
		if (regionLabels) {
			if (normalizedSlug === "eu") return regionLabels.eu;
			if (normalizedSlug === "us") return regionLabels.us;
		}
		if (normalizedSlug.includes("eu")) return "EU";
		if (normalizedSlug.includes("us")) return "US";
		return channel.name?.trim() || channel.currencyCode || channel.slug.toUpperCase();
	};

	const getPathForChannel = (newChannel: string) => {
		const segments = pathname.split("/").filter(Boolean);
		if (segments.length < 2) {
			return `/${newChannel}`;
		}
		segments[1] = newChannel;
		return `/${segments.join("/")}`;
	};

	return (
		<select
			id={id}
			aria-label={ariaLabel}
			aria-labelledby={ariaLabelledBy}
			className={cn(
				"h-10 w-fit rounded-md border border-input bg-background px-4 py-2 pr-10 text-sm",
				"text-foreground",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
				"disabled:cursor-not-allowed disabled:opacity-50",
				className,
			)}
			onChange={(e) => {
				const newChannel = e.currentTarget.value;
				const locale = params.locale ?? "en";
				setGeoPrefCookie({ locale, channel: newChannel });
				return router.push(getPathForChannel(newChannel));
			}}
			value={params.channel ?? currentChannel}
		>
			{effectiveChannels.map((channel) => {
				const label = getChannelLabel(channel);
				return (
					<option key={channel.id} value={channel.slug}>
						{showOptionEmoji ? `${iso2ToFlagEmoji(channelSlugToFlagIso(channel.slug))} ${label}` : label}
					</option>
				);
			})}
		</select>
	);
};
